/* JC-EVO 10/11/2026 cart replacement (Eric): pure cart operations, injectable for offline tests. */
(function (root, factory) {
  var api = factory();
  root.JcCartCore = api;
  if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  function findLine(cart, key) {
    var items = (cart && cart.items) || [];
    var index = items.findIndex(function (item) { return item.key === key; });
    return index < 0 ? null : { line: index + 1, item: items[index] };
  }
  async function settleExternal(getPending, { timeoutMs = 8000, maxRounds = 5 } = {}) {
    try {
      for (var round = 0; round < maxRounds; round++) {
        var pending = getPending() || [];
        if (!pending.length) return;
        await Promise.all(pending.map(function (promise) {
          return new Promise(function (resolve) {
            var timer = setTimeout(resolve, Math.max(0, timeoutMs));
            Promise.resolve(promise).then(done, done);
            function done() { clearTimeout(timer); resolve(); }
          });
        }));
      }
    } catch (_) {} // Settlement is bounded and never rejects; the caller checks readiness.
  }
  // JC-EVO 10/11/2026 RC20 p7 (Sol review): a completed write also invalidates a cart snapshot.
  function staleError() { var error = new Error('Cart snapshot changed'); error.jcStale = true; return error; }
  function createQueue(options) {
    var tail = Promise.resolve(), entries = options.log || [], timeoutMs = options.timeoutMs == null ? 8000 : options.timeoutMs;
    var getPending = options.externalPending || function () { return []; };
    var getEpoch = options.mutationEpoch || function () { return 0; };
    function ready() { return !getPending().length; }
    return { run: function (job) {
      var result = tail.then(async function () {
        var start = Date.now(), line, note = '', ok = false;
        try {
          var value, executed = false;
          for (var round = 0; round < 3; round++) {
            await settleExternal(getPending, { timeoutMs: timeoutMs });
            var epoch = getEpoch();
            var current = function () { return ready() && getEpoch() === epoch; };
            var context = { ready: current };
            if (job.key) {
              var cart = await options.fetchCart(), found = findLine(cart, job.key);
              // JC-EVO 10/11/2026 RC20 p9 (Sol re-review): use the verified successor identity after following subscription aliases.
              var intentIdentity = job.intentItem && identity(job.intentItem);
              if (!found && job.resolveKey) {
                var successor = job.resolveKey(job.key);
                found = findLine(cart, typeof successor === 'string' ? successor : successor && successor.key);
                if (successor && successor.identity) intentIdentity = successor.identity;
              }
              if (!found && intentIdentity) {
                var matches = (cart.items || []).filter(function (item) { return identity(item) === intentIdentity; });
                if (matches.length === 1) found = findLine(cart, matches[0].key);
                else if (matches.length > 1) throw new Error('Ambiguous quantity intent after cart re-key');
              }
              if (!current()) continue;
              if (!found) {
                if (job.onDropped) await job.onDropped();
                note = 'key dropped'; ok = true;
                return { dropped: true };
              }
              line = found.line;
              context = { line: line, item: found.item, cart: cart, ready: current };
            }
            if (!current()) continue;
            // Exec rechecks current() at the synchronous request start; retry if it was invalidated.
            try { value = await job.exec(context); executed = true; break; }
            catch (error) { if (!error.jcStale) throw error; }
          }
          if (!executed) throw new Error('External cart writes still pending or cart snapshot changed');
          ok = true;
          return value;
        } catch (error) { note = String(error && error.message || error); throw error; }
        finally {
          entries.push({ t: start, label: job.label, key: job.key || null, line: line || null, ok: ok, ms: Date.now() - start, note: note });
          if (entries.length > 50) entries.splice(0, entries.length - 50);
        }
      });
      tail = result.catch(function () {});
      return result;
    } };
  }
  function planChangeBody(opts) {
    var body = { line: opts.line, quantity: opts.item.quantity, selling_plan: opts.planId || null };
    if (opts.kind !== 'frequency') body.properties = Object.assign({}, opts.item.properties || {}, { _jc_sub_switch: opts.kind });
    return body;
  }
  function identity(item) {
    var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan.id || item.selling_plan || null;
    var properties = item.properties || {};
    return JSON.stringify([String(item.variant_id), plan == null ? null : String(plan),
      Object.keys(properties).sort().map(function (key) { return [key, properties[key]]; })]);
  }
  function totals(cart) {
    var result = new Map();
    ((cart && cart.items) || []).forEach(function (item) {
      var id = identity(item);
      result.set(id, (result.get(id) || 0) + item.quantity);
    });
    result.forEach(function (quantity, id) { if (quantity === 0) result.delete(id); });
    return result;
  }
  function verifyChange(before, after, change) {
    var expected = totals(before), actual = totals(after), item = change.item;
    function add(line, quantity) {
      var id = identity(line), next = (expected.get(id) || 0) + quantity;
      if (next === 0) expected.delete(id); else expected.set(id, next);
    }
    if (Object.prototype.hasOwnProperty.call(change, 'quantity')) add(item, change.quantity - item.quantity);
    else {
      add(item, -item.quantity);
      add(Object.assign({}, item, { properties: change.properties || item.properties || {}, selling_plan: change.planId || null,
        selling_plan_allocation: change.planId == null ? null : { selling_plan: { id: change.planId } } }), item.quantity);
    }
    return expected.size === actual.size && Array.from(expected).every(function (entry) { return actual.get(entry[0]) === entry[1]; });
  }
  function protectionPlan(cart, opts) {
    var lines = ((cart && cart.items) || []).filter(function (item) { return opts.isProtection ? opts.isProtection(item) : String(item.handle || '').indexOf(opts.handlePrefix) === 0; });
    var keep = opts.want && opts.variantId ? lines.find(function (item) { return String(item.variant_id) === String(opts.variantId); }) : null;
    var removeKeys = lines.filter(function (item) { return item !== keep; }).map(function (item) { return item.key; });
    var fixQty = keep && keep.quantity !== 1 ? (function () { var out = {}; out[keep.key] = 1; return out; })() : null;
    var add = opts.want && opts.variantId && !keep ? opts.variantId : null;
    return { removeKeys: removeKeys, add: add, fixQty: fixQty, matched: !removeKeys.length && !fixQty && !add && (!opts.want || !!keep) };
  }
  // JC-EVO 10/11/2026 RC20 p9b (gate redesign): match checkCart arithmetic exactly without its cart/storage side effects.
  function protectionLine(item, handle) {
    var value = String(item.handle || '');
    return value.includes('navidium-shipping-protection') || (!!handle && value.includes(handle));
  }
  function exclusions(config) {
    try { return JSON.parse(config.product_exclusion) || {}; } catch (_) { return {}; }
  }
  function protectionTotal(cart, config, handle) {
    if (!cart.items.length) return 0;
    var total = parseFloat(cart.total_price), excluded = exclusions(config);
    cart.items.forEach(function (item) {
      if (protectionLine(item, handle)) total -= item.final_line_price;
      else if (excluded.length >= 0) {
        excluded.forEach(function (sku) { if (item.sku === sku && config.exclusion_action != '0') total -= item.final_line_price; });
      } else {
        (excluded.sku || []).forEach(function (sku) { if (item.sku === sku) total -= item.final_line_price; });
        (excluded.types || []).forEach(function (type) { if (item.product_type === type) total -= item.final_line_price; });
      }
    });
    return total;
  }
  function protectionEligible(cart, config, country) {
    if (!config.success || config.show_on_cart === '0') return false;
    if (config.nvd_international == 0 && !config.nvd_nations?.some(function (nation) { return nation.name === country; })) return false;
    var excluded = exclusions(config);
    if (config.exclusion_action == '0' && cart.items.some(function (item) {
      return (excluded.sku || []).includes(item.sku) || (excluded.types || []).includes(item.product_type);
    })) return false;
    return true;
  }
  function clickMarkers(storage, now) {
    function read() {
      var data = {}, time = typeof now === 'function' ? now() : (now == null ? Date.now() : now);
      try { data = JSON.parse(storage && storage.getItem('jc_upsell_click') || '{}'); } catch (_) {}
      if (!data || Array.isArray(data) || typeof data !== 'object') data = {};
      Object.keys(data).forEach(function (key) { if (!Number.isFinite(data[key]) || time - data[key] > 86400000) delete data[key]; });
      try { if (storage) storage.setItem('jc_upsell_click', JSON.stringify(data)); } catch (_) {}
      return { data: data, time: time };
    }
    return {
      mark: function (id) { var entry = read(); entry.data[id] = entry.time; try { if (storage) storage.setItem('jc_upsell_click', JSON.stringify(entry.data)); } catch (_) {} },
      has: function (id) { return Object.prototype.hasOwnProperty.call(read().data, id); }
    };
  }
  function viewcartRequested(search) {
    var value = new URLSearchParams(search).get('viewcart');
    return (value == null ? '' : value.replace(/[^\w. -]/gi, '').trim()) === 'true';
  }
  function stripViewcart(href) {
    var url = new URL(href); url.searchParams.delete('viewcart'); return url.href;
  }
  function feeCents(price, rate) {
    var fee = Number(price) * 100 * (rate || 1);
    return Number.isFinite(fee) && fee > 0 ? Math.round(fee) : 0;
  }
  function cartSignature(state) {
    return JSON.stringify(((state && state.items) || []).map(function (item) { return [item.key, item.quantity, item.selling_plan || (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan.id) || null]; }));
  }
  function normalizeRecommendation(p) {
    return { id: p.id, handle: p.handle, title: p.title, url: p.url, image: imageUrl(p.featured_image), available: p.available,
      variants: (p.variants || []).map(function (v) { return { id: v.id, title: v.title, available: v.available, price: Number(v.price), compare_at_price: v.compare_at_price == null ? null : Number(v.compare_at_price), image: imageUrl(v.featured_image) }; }) };
  }
  function imageUrl(value) { return value && typeof value === 'object' ? value.src || value.url || null : value || null; }
  function localizeUrl(url, root) {
    root = (root || '/').replace(/\/$/, '') || '/';
    if (root === '/' || !url || url[0] !== '/' || url[1] === '/') return url;
    var next = url.slice(root.length, root.length + 1);
    if (url.slice(0, root.length) === root && (!next || next === '/' || next === '?' || next === '#')) return url;
    return url === '/' ? root : root + url;
  }
  return { staleError: staleError, createQueue: createQueue, findLine: findLine, planChangeBody: planChangeBody, settleExternal: settleExternal, verifyChange: verifyChange, identity: identity, protectionPlan: protectionPlan, protectionLine: protectionLine, protectionTotal: protectionTotal, protectionEligible: protectionEligible,
    clickMarkers: clickMarkers, viewcartRequested: viewcartRequested, stripViewcart: stripViewcart, feeCents: feeCents,
    cartSignature: cartSignature, normalizeRecommendation: normalizeRecommendation, localizeUrl: localizeUrl };
});
